import dayjs from 'dayjs'
import logoBase64 from '@/assests/shakthiLogo'
import type { Agreement, AgreementAmendment } from '@/types'

interface AgreementParty {
  role: string
  name: string
  rep?: string
  designation?: string
  email?: string
  phone?: string
  address?: string
}

interface AgreementPdfData {
  agreement: Agreement & { agreementType?: string[]; parties?: AgreementParty[] }
  code: string
}

const fmtDate = (d?: Date | string) => (d ? dayjs(d).format('DD-MM-YYYY') : '—')

export const generateAgreementHTML = (data: AgreementPdfData): string => {
  const { agreement, code } = data
  const parties = agreement.parties ?? []
  const types = agreement.agreementType?.length ? agreement.agreementType.join(', ') : agreement.templateType
  const amendments: AgreementAmendment[] = agreement.amendments ?? []

  const partiesRows = parties
    .map(
      (p) => `
      <tr>
        <td style="border:1px solid #ccc;padding:8px;font-weight:600;">${p.role || ''}</td>
        <td style="border:1px solid #ccc;padding:8px;">${p.name || ''}</td>
        <td style="border:1px solid #ccc;padding:8px;">${p.rep || ''}${p.designation ? ` (${p.designation})` : ''}</td>
        <td style="border:1px solid #ccc;padding:8px;">${p.email || ''}${p.phone ? `<br/>${p.phone}` : ''}</td>
        <td style="border:1px solid #ccc;padding:8px;">${p.address || ''}</td>
      </tr>`
    )
    .join('')

  const amendmentsRows = amendments
    .map(
      (a) => `
      <tr>
        <td style="border:1px solid #ccc;padding:8px;">Rev ${a.revisionNumber}</td>
        <td style="border:1px solid #ccc;padding:8px;">${a.changeDescription || ''}</td>
        <td style="border:1px solid #ccc;padding:8px;">${fmtDate(a.validFrom)} – ${fmtDate(a.validTo)}</td>
        <td style="border:1px solid #ccc;padding:8px;">${a.signedAt ? `Signed ${fmtDate(a.signedAt)}` : 'Unsigned'}</td>
      </tr>`
    )
    .join('')

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="UTF-8">
  <title>Agreement - ${code}</title>
  <style>
    * { margin: 0; padding: 0; box-sizing: border-box; }
    @page { margin: 15mm; }
    body { font-family: 'Segoe UI', Arial, sans-serif; color: #000; }
    .header { display: flex; justify-content: space-between; align-items: flex-start; margin-bottom: 24px; }
    .header img { height: 55px; }
    .title { font-size: 26px; font-weight: 700; color: #001A4D; text-align: right; }
    .meta { font-size: 12px; color: #555; text-align: right; margin-top: 6px; }
    .section-title {
      font-size: 13px; font-weight: bold; color: #fff; background: #001A4D;
      padding: 6px 12px; margin: 20px 0 10px; border-radius: 3px;
    }
    table { width: 100%; border-collapse: collapse; font-size: 12px; }
    th { background: #001A4D; color: #fff; padding: 8px; text-align: left; border: 1px solid #001A4D; }
    .kv-table td { padding: 6px 10px; border-bottom: 1px solid #eee; font-size: 12.5px; }
    .kv-table td:first-child { font-weight: 600; color: #555; width: 220px; }
    .notes { margin-top: 16px; font-size: 12.5px; line-height: 1.6; background: #f8fafc; padding: 12px; border-left: 4px solid #001A4D; }
    .footer { margin-top: 40px; text-align: center; font-size: 10px; color: #666; border-top: 1px solid #ddd; padding-top: 10px; }
  </style>
</head>
<body>
  <div class="header">
    <img src="${logoBase64}" alt="Shakthi Power" />
    <div>
      <div class="title">AGREEMENT</div>
      <div class="meta">
        Code: ${code}<br/>
        Type: ${types || '—'}<br/>
        Status: ${agreement.status}
      </div>
    </div>
  </div>

  <div class="section-title">Agreement Details</div>
  <table class="kv-table">
    <tr><td>Agreement Date</td><td>${fmtDate(agreement.startDate)}</td></tr>
    <tr><td>Expiry Date</td><td>${fmtDate(agreement.endDate)}</td></tr>
    ${agreement.validityDays ? `<tr><td>Validity</td><td>${agreement.validityDays} days</td></tr>` : ''}
    ${agreement.stampPaperValue ? `<tr><td>Stamp Paper Value</td><td>₹${agreement.stampPaperValue}</td></tr>` : ''}
    ${agreement.stampPaperNumber ? `<tr><td>Stamp Paper Number</td><td>${agreement.stampPaperNumber}</td></tr>` : ''}
    ${agreement.placeOfExecution ? `<tr><td>Place of Execution</td><td>${agreement.placeOfExecution}</td></tr>` : ''}
    ${agreement.signedAt ? `<tr><td>Signed On</td><td>${fmtDate(agreement.signedAt)}</td></tr>` : ''}
  </table>

  ${parties.length ? `
  <div class="section-title">Parties</div>
  <table>
    <thead>
      <tr><th>Role</th><th>Name</th><th>Representative</th><th>Contact</th><th>Address</th></tr>
    </thead>
    <tbody>${partiesRows}</tbody>
  </table>` : ''}

  ${agreement.notes ? `<div class="notes"><strong>Notes:</strong> ${agreement.notes}</div>` : ''}

  ${amendments.length ? `
  <div class="section-title">Amendments</div>
  <table>
    <thead>
      <tr><th>Revision</th><th>Change</th><th>Valid Period</th><th>Status</th></tr>
    </thead>
    <tbody>${amendmentsRows}</tbody>
  </table>` : ''}

  <div class="footer">
    Shakthi Power — Mantyralayam Tower, No 35 B 2nd Floor, Dr Rajendra Prasad Rd, Hasthinapuram, Chromepet, Chennai, 600044<br/>
    E-mail: info@shakthipower.com &nbsp;|&nbsp; Web: www.shakthipower.com
  </div>
</body>
</html>`
}

/**
 * Opens the browser's print dialog on a purpose-built agreement document
 * (hidden iframe) — never prints the live app page/chrome.
 */
export const printAgreementPDF = (htmlContent: string) => {
  const blob = new Blob([htmlContent], { type: 'text/html; charset=utf-8' })
  const url = URL.createObjectURL(blob)

  const iframe = document.createElement('iframe')
  iframe.style.cssText = 'position:fixed;right:0;bottom:0;width:0;height:0;border:0;'
  document.body.appendChild(iframe)

  const doc = iframe.contentWindow?.document
  if (!doc) {
    const w = window.open('', '_blank')
    if (w) {
      w.document.write(htmlContent)
      w.document.close()
      w.focus()
      setTimeout(() => w.print(), 900)
    }
    URL.revokeObjectURL(url)
    return
  }

  doc.open()
  doc.write(htmlContent)
  doc.close()

  iframe.onload = () => {
    setTimeout(() => {
      iframe.contentWindow?.focus()
      iframe.contentWindow?.print()
      setTimeout(() => {
        document.body.removeChild(iframe)
        URL.revokeObjectURL(url)
      }, 2500)
    }, 700)
  }
}

